import { useEffect, useRef } from 'react';

/**
 * Hook that adds the `in-view` class to an element when it scrolls into the viewport.
 * Uses IntersectionObserver — observes once, then stops.
 *
 * Pass a `resetKey` (e.g. route pathname) to reset and re-observe when the key changes.
 * This is needed for layout-level components (e.g. Footer) that persist across route changes.
 *
 * Usage:
 *   const ref = useAnimateOnScroll();
 *   const ref = useAnimateOnScroll(0.2, pathname); // re-animates on each route
 *   <div ref={ref} className="slide-up">...</div>
 */
export function useAnimateOnScroll(threshold = 0.1, resetKey) {
  const ref = useRef(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    element.classList.remove('in-view');

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > threshold) {
            entry.target.classList.add('in-view');
            observer.unobserve(entry.target);
          }
        });
      },
      { root: null, threshold }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [threshold, resetKey]);

  return ref;
}
